// Daten und Handgriffe der Tafel — gemeinsam für die Seite und die Kachel im Dashboard.
import { useCallback, useEffect, useMemo, useState } from 'react';
import { api, ApiFehler } from '../../../gemeinsam/lib/api';
import { euro } from '../../../gemeinsam/lib/daten';
import { useSprache, type Schluessel } from '../../../gemeinsam/lib/i18n';
import { meldungZeigen } from '../../../gemeinsam/komponenten/Meldungen';
import { einheitAnzeige } from '../../artikel/lib/einheiten';
import type { Aufgabe, AutoAufgabe, Frist } from '../../../gemeinsam/lib/typen';
import type { Person } from './aufgabenEingabe';
import { eintraegeBauen, tag, plus, type Eintrag } from './tafelDaten';

/** Typen, für die es Texte gibt — ein neuer Typ vom Server ohne Text bleibt unsichtbar statt leer. */
export const BEKANNT = new Set(['nachbestellen', 'ausliefern', 'nachfassen', 'entwurf', 'support', 'belegscan',
  'mahnen', 'bezahlen', 'zuordnen', 'bank', 'ustva']);

// „bis Do., 15.10." + „." am Satzende ergäbe „15.10.." — doppelten Punkt kappen.
export const fuellen = (text: string, werte: Record<string, unknown>) =>
  text.replace(/\{(\w+)\}/g, (_, k: string) => (werte[k] ?? '') === '' ? '' : String(werte[k])).replace(/\.\.$/, '.');
// Leere Werte hinterlassen sonst „ · 12,00 €" oder „an ".
const sauber = (s: string) => s.replace(/^[\s·]+|[\s·]+$/g, '').replace(/(\s·)+\s·/g, ' ·');

/** Datums- und Zetteltexte in der eingestellten Sprache. */
export function useTafelTexte() {
  const { t, sprache } = useSprache();
  const heute = tag(new Date());
  const morgen = plus(heute, 1);

  return useMemo(() => {
    const tagKurz = (d: string) =>
      new Date(`${d}T12:00:00`).toLocaleDateString(sprache, { weekday: 'short', day: 'numeric', month: 'numeric' });
    const tagLang = (d: string) => (d === heute ? t('aufgaben.heute') : d === morgen ? t('aufgaben.morgen') : tagKurz(d));
    const monatName = (m: string) => new Date(`${m}-15T12:00:00`).toLocaleDateString(sprache, { month: 'long', year: 'numeric' });
    const zahl = (n: unknown) => Number(n).toLocaleString(sprache, { maximumFractionDigits: 3 });

    function autoTexte(a: AutoAufgabe): { titel: string; text: string } {
      const w = a.werte;
      const werte: Record<string, unknown> = {
        ...w,
        betrag: w.betrag !== undefined ? euro(Number(w.betrag)) : w.zahllast !== undefined ? euro(Number(w.zahllast)) : '',
        bestand: w.bestand !== undefined ? zahl(w.bestand) : '',
        mindestbestand: w.mindestbestand !== undefined ? zahl(w.mindestbestand) : '',
        einheit: w.einheit ? einheitAnzeige(String(w.einheit), t) : '',
        datum: a.faellig ? tagKurz(a.faellig) : '',
        monat: w.monat ? monatName(String(w.monat)) : '',
      };
      const f = (k: string) => sauber(fuellen(t(k as Schluessel), werte));
      const titel = f(`aufgaben.auto.${a.typ}`);
      if (a.typ === 'nachbestellen') {
        return { titel, text: [f('aufgaben.auto.nachbestellenText'), w.lieferant ? f('aufgaben.auto.bei') : ''].filter(Boolean).join(' · ') };
      }
      if (a.typ === 'bank') {
        return { titel, text: w.abgelaufen ? t('aufgaben.auto.bankAbgelaufen') : fuellen(t('aufgaben.auto.bankEndet'), { datum: w.bis ? tagKurz(String(w.bis)) : '' }) };
      }
      return { titel, text: f(`aufgaben.auto.${a.typ}Text`) };
    }

    function fristTexte(fr: Frist): { titel: string; text: string } {
      const w = fr.werte;
      const werte: Record<string, unknown> = {
        ...w, betrag: w.betrag !== undefined && w.betrag !== null ? euro(Number(w.betrag)) : '', monat: w.monat ? monatName(String(w.monat)) : '',
      };
      const f = (k: string) => sauber(fuellen(t(k as Schluessel), werte));
      return { titel: f(`tafel.frist.${fr.typ}`), text: f(`tafel.frist.${fr.typ}Text`) };
    }

    /** Titel und Zusatz eines beliebigen Zettels. */
    function zettelTexte(e: Eintrag): { titel: string; text: string } {
      if (e.aufgabe) return { titel: e.aufgabe.title, text: e.aufgabe.ort || '' };
      if (e.auto) return autoTexte(e.auto);
      if (e.frist) return fristTexte(e.frist);
      return { titel: '', text: '' };
    }

    return { heute, morgen, tagKurz, tagLang, autoTexte, fristTexte, zettelTexte };
  }, [t, sprache, heute, morgen]);
}

/** Lädt Aufgaben, automatische Aufgaben und Fristen für einen Zeitraum. */
export function useTafel({ von, bis }: { von: string; bis: string }) {
  const { t } = useSprache();
  const [aufgaben, setAufgaben] = useState<Aufgabe[] | null>(null);
  const [auto, setAuto] = useState<AutoAufgabe[]>([]);
  const [fristen, setFristen] = useState<Frist[]>([]);
  const [team, setTeam] = useState<Person[]>([]);
  const [fehler, setFehler] = useState<string | null>(null);
  const [tarifhinweis, setTarifhinweis] = useState<string | null>(null);

  const ladeAufgaben = useCallback(async () => {
    try { setAufgaben(await api<Aufgabe[]>('/tasks')); }
    catch (e) { setFehler(e instanceof ApiFehler ? e.message : 'Aufgaben konnten nicht geladen werden.'); setAufgaben(l => l ?? []); }
  }, []);

  useEffect(() => {
    void ladeAufgaben();
    // Fehlt hier etwas, bleibt der Rest nutzbar.
    api<AutoAufgabe[]>('/tasks/automatisch').then(l => setAuto(l.filter(a => BEKANNT.has(a.typ)))).catch(() => setAuto([]));
    api<{ id: string; name: string; username: string; aktiv?: boolean }[]>('/users')
      .then(u => setTeam(u.filter(x => x.aktiv !== false).map(x => ({ id: x.id, name: x.name, username: x.username }))))
      .catch(() => setTeam([]));
  }, [ladeAufgaben]);

  useEffect(() => {
    let aktiv = true;
    api<Frist[]>(`/tasks/fristen?von=${von}&bis=${bis}`)
      .then(l => { if (aktiv) setFristen(l); })
      .catch(() => { if (aktiv) setFristen([]); });
    return () => { aktiv = false; };
  }, [von, bis]);

  const eintraege = useMemo(() => eintraegeBauen(aufgaben ?? [], auto, fristen, tag(new Date())), [aufgaben, auto, fristen]);

  const ersetzen = (neu: Aufgabe) => setAufgaben(l => (l ?? []).map(x => x.id === neu.id ? neu : x));

  async function aendern(a: Aufgabe, daten: Partial<Aufgabe>): Promise<Aufgabe | null> {
    const vorher = aufgaben;
    setAufgaben(l => (l ?? []).map(x => x.id === a.id ? { ...x, ...daten } : x));
    setFehler(null);
    try {
      const neu = await api<Aufgabe>(`/tasks/${a.id}`, { methode: 'PUT', daten });
      ersetzen(neu);
      if ((a.column || 'todo') !== 'done' && neu.column === 'done') {
        // Wiederkehrend: der Server legt die nächste an — die holen wir.
        if (a.wiederholung && !a.folgeId) {
          const frisch = await api<Aufgabe[]>('/tasks');
          setAufgaben(frisch);
          const folge = frisch.find(x => x.vorgaengerId === a.id);
          meldungZeigen(folge?.dueDate
            ? fuellen(t('aufgaben.folge'), { datum: new Date(`${folge.dueDate}T12:00:00`).toLocaleDateString() })
            : t('aufgaben.erledigtMeldung'));
        } else {
          meldungZeigen(t('aufgaben.erledigtMeldung'));
        }
      }
      return neu;
    } catch (e) {
      setAufgaben(vorher ?? []);
      setFehler(e instanceof ApiFehler ? e.message : 'Speichern fehlgeschlagen.');
      return null;
    }
  }

  async function anlegen(daten: Partial<Aufgabe> & { quelle?: string }): Promise<Aufgabe | null> {
    setFehler(null);
    try {
      const neu = await api<Aufgabe>('/tasks', { methode: 'POST', daten });
      setAufgaben(l => [...(l ?? []), neu]);
      return neu;
    } catch (e) {
      setFehler(e instanceof ApiFehler ? e.message : 'Anlegen fehlgeschlagen.');
      return null;
    }
  }

  async function loeschen(a: Aufgabe) {
    try {
      await api(`/tasks/${a.id}`, { methode: 'DELETE' });
      setAufgaben(l => (l ?? []).filter(x => x.id !== a.id));
      return true;
    } catch (e) {
      setFehler(e instanceof ApiFehler ? e.message : 'Löschen fehlgeschlagen.');
      return false;
    }
  }

  async function timer(a: Aufgabe) {
    setTarifhinweis(null);
    try {
      ersetzen(await api<Aufgabe>(`/tasks/${a.id}/timer/${a.timerRunning ? 'stop' : 'start'}`, { methode: 'POST' }));
    } catch (e) {
      // Zeiterfassung gibt es erst ab Professional — eine Tarifgrenze, kein Fehler.
      if (e instanceof ApiFehler && e.istTarifgrenze) setTarifhinweis(`${e.message} Verfügbar ab dem Tarif ${e.daten?.upgradeTo ?? 'Professional'}.`);
      else setFehler(e instanceof ApiFehler ? e.message : 'Timer fehlgeschlagen.');
    }
  }

  const autoWeg = (schluessel: string) => setAuto(l => l.filter(x => x.schluessel !== schluessel));

  async function autoAktion(a: AutoAufgabe) {
    if (!a.aktion) return;
    setFehler(null);
    try {
      if (a.aktion.art === 'lieferschein_geliefert') await api(`/belege/${a.aktion.id}/status`, { methode: 'POST', daten: { status: 'geliefert' } });
      else await api(`/expenses/${a.aktion.id}/bezahlt`, { methode: 'POST', daten: {} });
      autoWeg(a.schluessel);
      meldungZeigen(t('aufgaben.erledigtMeldung'));
    } catch (e) {
      setFehler(e instanceof ApiFehler ? e.message : 'Das hat nicht geklappt.');
    }
  }

  async function autoZurueckstellen(a: AutoAufgabe, tage: 1 | 3 | 7) {
    try {
      const r = await api<{ bis: string }>('/tasks/automatisch/ruhe', { methode: 'POST', daten: { schluessel: a.schluessel, tage } });
      autoWeg(a.schluessel);
      meldungZeigen(fuellen(t('aufgaben.zurueckgestellt'), { datum: new Date(r.bis).toLocaleDateString() }));
    } catch (e) {
      setFehler(e instanceof ApiFehler ? e.message : 'Zurückstellen fehlgeschlagen.');
    }
  }

  return {
    aufgaben, auto, fristen, team, eintraege, fehler, setFehler, tarifhinweis,
    aendern, anlegen, loeschen, timer, autoWeg, autoAktion, autoZurueckstellen,
  };
}

export type Tafel = ReturnType<typeof useTafel>;
